import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Storage } from '@ionic/storage';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import {
  BranchDeepLinks,
  BranchInitEvent,
} from 'capacitor-branch-deep-links';

/**
 * Deep linking on Capacitor.
 *
 * In practice Branch rewrites the shared https://www.kgdiamonds.com/<stoneName>
 * link to its own scheme before it reaches us, e.g.
 *   kgdiamonds://open?_branch_referrer=<gzip+base64>&link_click_id=...
 * where `_branch_referrer` gunzips to a URL carrying the real params, e.g.
 *   https://kg-diamonds.app.link/?$stoneName=N0516480&$refKgCompanyId=null
 * Branch's own `init` listener is meant to decode that for us, but it has been
 * firing `initError` instead, so we decode `_branch_referrer` ourselves.
 *
 * Delivery paths, all feeding the same routing logic:
 *   - @capacitor/app `appUrlOpen` (running) + the cold-start launch URL (via
 *     resolveColdStartRoute, so the deep link wins the initial navigation
 *     instead of racing the home redirect), and
 *   - Branch's `init` listener, if/when it succeeds.
 *
 * Resolution (matching the old Android guard):
 *   - logged-in user  -> open the product page (/:id)
 *   - logged-out user -> stash it; the login flow consumes
 *     `deeplink-stoneName` / `deeplink-location` after sign-in.
 */
@Injectable({ providedIn: 'root' })
export class DeepLinkService {
  private initialized = false;
  private launchUrlHandled = false;

  constructor(
    private router: Router,
    private storage: Storage
  ) {}

  /** Call once on app startup (native only). Safe to call multiple times. */
  init(): void {
    if (this.initialized || !Capacitor.isNativePlatform()) {
      return;
    }
    this.initialized = true;

    BranchDeepLinks.addListener('init', (event: BranchInitEvent) => {
      this.handleReferringParams(event.referringParams);
    });

    BranchDeepLinks.addListener('initError', (error: any) => {
      console.error('[Branch] init error', error);
    });

    // Direct App Link / Universal Link (www.kgdiamonds.com/<stoneName>) opened
    // while the app is already running. (The cold-start case is handled by the
    // default route guard via resolveColdStartRoute(), so it wins the initial
    // navigation instead of racing the home redirect.)
    App.addListener('appUrlOpen', data => {
      this.handleUrl(data.url);
    });
  }

  /**
   * Cold-start entry point for the default route guard. If the app was launched
   * from a www.kgdiamonds.com/<stoneName> link, returns the product route to
   * redirect to (logged in) or stashes it for the login flow and returns null.
   * Returns null on every later call so normal home routing is unaffected.
   */
  async resolveColdStartRoute(): Promise<string | null> {
    if (this.launchUrlHandled || !Capacitor.isNativePlatform()) {
      return null;
    }
    this.launchUrlHandled = true;

    let url: string | undefined;
    try {
      url = (await App.getLaunchUrl())?.url;
    } catch {
      return null;
    }
    if (!url) {
      return null;
    }

    const { stoneName, refKgCompanyId } = await this.extractProduct(url);
    if (!stoneName) {
      return null;
    }

    const user = await this.storage.get('loggedInUser');
    if (user) {
      return '/' + stoneName;
    }
    // Logged out: stash for the login flow and let the guard route home.
    await this.storage.set('deeplink-stoneName', stoneName);
    if (refKgCompanyId) {
      await this.storage.set('deeplink-location', refKgCompanyId);
    }
    return null;
  }

  /** Handle a raw deep-link URL delivered by @capacitor/app while running. */
  private async handleUrl(url: string): Promise<void> {
    const { stoneName, refKgCompanyId } = await this.extractProduct(url);
    await this.routeToProduct(stoneName, refKgCompanyId);
  }

  private async handleReferringParams(params: any): Promise<void> {
    if (!params) {
      return;
    }

    // Two link shapes are supported:
    //   - legacy Branch links carrying $stoneName / $refKgCompanyId params
    //   - the current shared link, https://www.kgdiamonds.com/<stoneName>,
    //     where the stone is the last path segment of the referring URL.
    const { stoneName, refKgCompanyId } = this.resolveProduct(params);
    await this.routeToProduct(stoneName, refKgCompanyId);
  }

  /** Open the product now (logged in) or stash it for the login flow. */
  private async routeToProduct(
    stoneName: string | null,
    refKgCompanyId: string | null
  ): Promise<void> {
    // Only act on links that actually point at a product.
    if (!stoneName) {
      return;
    }

    const user = await this.storage.get('loggedInUser');
    if (user) {
      // The product page lives at /:id (the /products/... routes redirect here),
      // so the stone alone is enough to open it.
      this.router.navigate(['/', stoneName]);
    } else {
      // Defer: the login flow reads these and forwards to the product.
      await this.storage.set('deeplink-stoneName', stoneName);
      if (refKgCompanyId) {
        await this.storage.set('deeplink-location', refKgCompanyId);
      }
    }
  }

  /**
   * Resolve the product from Branch referring params, supporting both the
   * explicit $stoneName param and the path-based www.kgdiamonds.com/<stoneName>
   * link (read from whichever URL field Branch populates).
   */
  private resolveProduct(params: any): {
    stoneName: string | null;
    refKgCompanyId: string | null;
  } {
    if (params.$stoneName) {
      return {
        stoneName: String(params.$stoneName),
        refKgCompanyId:
          params.$refKgCompanyId != null
            ? String(params.$refKgCompanyId)
            : null,
      };
    }

    const url =
      params['+url'] ||
      params.$canonical_url ||
      params['~referring_link'] ||
      params['+non_branch_link'] ||
      params.$deeplink_path ||
      params.$android_deeplink_path;

    return this.parseProductUrl(url);
  }

  /** Extract the stone (and optional location) from a kgdiamonds product URL. */
  private parseProductUrl(url: any): {
    stoneName: string | null;
    refKgCompanyId: string | null;
  } {
    if (!url || typeof url !== 'string') {
      return { stoneName: null, refKgCompanyId: null };
    }

    // Strip scheme/host/query so the same logic handles full URLs and bare
    // paths, then split into non-empty segments.
    const path = url.replace(/^[a-z]+:\/\/[^/]+/i, '').split(/[?#]/)[0];
    const segments = path
      .split('/')
      .map(s => s.trim())
      .filter(Boolean)
      .map(s => {
        try {
          return decodeURIComponent(s);
        } catch {
          return s;
        }
      });

    if (!segments.length) {
      return { stoneName: null, refKgCompanyId: null };
    }

    // Legacy: /products/<stoneName>/<stoneName>/<currentLocation>
    const productsIdx = segments.indexOf('products');
    if (productsIdx !== -1 && segments[productsIdx + 1]) {
      return {
        stoneName: segments[productsIdx + 1],
        refKgCompanyId: segments[productsIdx + 3] || null,
      };
    }

    // Current: /<stoneName>
    return { stoneName: segments[segments.length - 1], refKgCompanyId: null };
  }

  /**
   * Resolve the product from a raw deep-link URL, trying, in order:
   *   1. the gzipped Branch payload in `_branch_referrer` (the real-world case),
   *   2. $stoneName as a plain query param (e.g. app.link?$stoneName=...), and
   *   3. the www.kgdiamonds.com/<stoneName> path.
   */
  private async extractProduct(url: string): Promise<{
    stoneName: string | null;
    refKgCompanyId: string | null;
  }> {
    const empty = { stoneName: null, refKgCompanyId: null };
    if (!url) {
      return empty;
    }

    // 1. Branch scheme: the stone is gzipped inside _branch_referrer.
    const branchParams = await this.decodeBranchReferrer(url);
    if (branchParams) {
      const stone = this.normalize(branchParams.get('$stoneName'));
      if (stone) {
        return {
          stoneName: stone,
          refKgCompanyId: this.normalize(branchParams.get('$refKgCompanyId')),
        };
      }
    }

    // 2. $stoneName as a plain query param.
    const params = this.readParams(url);
    const queryStone = this.normalize(params.get('$stoneName'));
    if (queryStone) {
      return {
        stoneName: queryStone,
        refKgCompanyId: this.normalize(params.get('$refKgCompanyId')),
      };
    }

    // 3. Path-based web link.
    if (url.includes('kgdiamonds.com')) {
      return this.parseProductUrl(url);
    }

    return empty;
  }

  /** Decode the gzipped `_branch_referrer` payload into its query params. */
  private async decodeBranchReferrer(
    url: string
  ): Promise<URLSearchParams | null> {
    const match = url.match(/[?&]_branch_referrer=([^&]+)/);
    if (!match) {
      return null;
    }
    const inner = await this.gunzipBase64(decodeURIComponent(match[1]));
    return inner ? this.readParams(inner) : null;
  }

  /** Gunzip a base64 string using the WebView's DecompressionStream. */
  private async gunzipBase64(b64: string): Promise<string | null> {
    try {
      const binary = atob(b64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      const Decompression = (window as any).DecompressionStream;
      if (!Decompression) {
        console.error('[DeepLink] DecompressionStream unavailable');
        return null;
      }
      const stream = new Blob([bytes])
        .stream()
        .pipeThrough(new Decompression('gzip'));
      const buffer = await new Response(stream).arrayBuffer();
      return new TextDecoder().decode(buffer);
    } catch (e) {
      console.error('[DeepLink] gunzip failed', e);
      return null;
    }
  }

  /** Parse the query string of a URL (decodes %24 -> $ for Branch keys). */
  private readParams(urlOrQuery: string): URLSearchParams {
    const q = urlOrQuery.includes('?')
      ? urlOrQuery.slice(urlOrQuery.indexOf('?') + 1)
      : '';
    return new URLSearchParams(q);
  }

  /** Treat empty / "null" / "undefined" param values as absent. */
  private normalize(value: string | null): string | null {
    const t = (value || '').trim();
    return t && t !== 'null' && t !== 'undefined' ? t : null;
  }
}
